/**
 * Single shared auth-clear helper. Every logout / session-teardown path routes
 * through here so the device push token is never left registered to a stale
 * member on a shared device.
 */
import store from '@/store/store';
import { clearAuth } from '@/store/slices/authSlice';
import { queryClient } from '@/lib/queryClient';
import { cancelBillingRefresh } from '@/lib/billingRefresh';
import { clearRememberedList } from '@/utils/searchListRestore';
import { deregisterDeviceToken, deleteToken } from '@/services/pushRegistration';

interface ClearSessionOptions {
  /**
   * Skip the server-side device-token DELETE. Required on the 401/refresh-failure
   * path in apiClient.ts: issuing an authenticated DELETE with a dead token would
   * itself 401, which would call clearAuth() again and recurse into this helper.
   * The local plugin cleanup below still runs on that path — it needs no auth and
   * cannot recurse — so a device handed off after an involuntary logout doesn't
   * keep receiving the previous member's pushes.
   */
  skipServerDeregister?: boolean;
}

export async function clearSession(options: ClearSessionOptions = {}): Promise<void> {
  const { skipServerDeregister = false } = options;

  try {
    await deleteToken();
  } catch (error) {
    console.error('[clearSession] Failed to clear local push token:', error);
  }

  if (!skipServerDeregister) {
    // Awaited (bounded by a timeout) so the DELETE is sent while the auth
    // token clearAuth() is about to null out is still attached to requests.
    // No retry, and a failure must never block session clearing.
    await Promise.race([
      deregisterDeviceToken().catch((error) => {
        console.error('[clearSession] Failed to deregister device token:', error);
      }),
      new Promise<void>((resolve) => setTimeout(resolve, 2000)),
    ]);
  }

  store.dispatch(clearAuth());
  // A post-billing poll (D51f) is not tied to a screen, so nothing else stops it: cancel it before the cache it
  // reads and refreshes is cleared, or it would keep working for the next member of a shared device.
  cancelBillingRefresh(queryClient);
  queryClient.clear();
  // The remembered Search list (ids + scroll offset) is module state that outlives the cache clear: drop it so the
  // next member of a shared device never gets the previous member's list restore.
  clearRememberedList();
}
